// EDITAR O ELIMINAR LOS MOTIVOS DE CANCELACIÓN PROPIOS DE LA AGENCIA.
//
// Lissette 2026-10-22. Se abre desde la opción "⚙️ Editar personalizados…"
// del desplegable de motivo de cancelación. Los de fábrica no aparecen: no se
// pueden cambiar. Mismo guardado que "➕ Agregar otro…": la lista entera va a
// `cancelReasons` de los ajustes de la agencia.
import { useState } from 'react';
import { Modal, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { updateAgencySettings } from '../api.js';
import { getMotivosDeAgencia, setMotivosDeAgencia, renombrarMotivo, quitarMotivo } from '../motivosDeCancelacion.js';

export function GestorMotivosDeCancelacion({ lang = 'es', onClose, onChange }) {
  const es = lang === 'es';
  const [lista, setLista] = useState(getMotivosDeAgencia());
  const [editando, setEditando] = useState(null); // { de, a }
  const [ocupado, setOcupado] = useState(false);

  const guardar = async (siguiente, okMsg) => {
    setOcupado(true);
    const antes = getMotivosDeAgencia();
    setMotivosDeAgencia(siguiente);
    setLista(siguiente);
    if (typeof onChange === 'function') onChange();
    try {
      await updateAgencySettings({ cancelReasons: siguiente });
      toast.success(okMsg);
      return true;
    } catch (e) {
      // Si no se guardó se dice y se deshace: callarse haría creer que cambió.
      setMotivosDeAgencia(antes);
      setLista(antes);
      if (typeof onChange === 'function') onChange();
      toast.error((es ? 'No se pudo guardar: ' : 'Could not save: ') + (e.message || ''));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const renombrar = async () => {
    const r = renombrarMotivo(lista, editando.de, editando.a);
    if (r.error) {
      toast.error(r.error === 'repetido'
        ? (es ? 'Ya hay un motivo con ese nombre.' : 'A reason with that name already exists.')
        : r.error === 'largo' ? (es ? 'Máximo 60 caracteres.' : 'Max 60 characters.')
          : (es ? 'Escribe un nombre.' : 'Type a name.'));
      return;
    }
    if (await guardar(r.lista, es ? 'Motivo cambiado' : 'Reason renamed')) setEditando(null);
  };

  const quitar = async (m) => {
    const ok = await confirmDialog({
      message: es
        ? `¿Quitar "${m}" de la lista? Las pólizas que ya lo tienen lo conservan.`
        : `Remove "${m}" from the list? Policies that already have it keep it.`,
      danger: true,
    });
    if (!ok) return;
    await guardar(quitarMotivo(lista, m), es ? 'Motivo quitado de la lista' : 'Reason removed from the list');
  };

  return (
    <Modal title={es ? 'Motivos de cancelación personalizados' : 'Custom cancellation reasons'} onClose={() => !ocupado && onClose()}>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.8rem', lineHeight: 1.5 }}>
        {es
          ? 'Los motivos de fábrica no se pueden cambiar. Cambiar o quitar uno de estos no toca las pólizas que ya lo tienen.'
          : "Built-in reasons can't be changed. Renaming or removing one of these doesn't touch policies that already have it."}
      </div>
      {lista.length === 0 && (
        <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'No quedan motivos personalizados.' : 'No custom reasons left.'}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map((m) => (
          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.84rem', color: 'var(--c-white,#eef6ff)' }}>
            {editando?.de === m ? (
              <>
                <input className="dkt-inp" autoFocus value={editando.a} maxLength={60}
                  onChange={(e) => setEditando({ de: m, a: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') renombrar(); if (e.key === 'Escape') setEditando(null); }}
                  style={{ flex: 1 }} />
                <button type="button" className="dkt-btn-p" disabled={ocupado} onClick={renombrar}>{es ? 'Guardar' : 'Save'}</button>
                <button type="button" className="dkt-btn-g" disabled={ocupado} onClick={() => setEditando(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m}</span>
                <button type="button" className="dkt-btn-s" disabled={ocupado} title={es ? 'Cambiar el nombre' : 'Rename'}
                  onClick={() => setEditando({ de: m, a: m })}>✏️</button>
                <button type="button" className="dkt-btn-s" disabled={ocupado} title={es ? 'Quitar de la lista' : 'Remove from list'}
                  onClick={() => quitar(m)}>🗑️</button>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button type="button" className="dkt-btn-g" onClick={onClose} disabled={ocupado}>{es ? 'Cerrar' : 'Close'}</button>
      </div>
    </Modal>
  );
}
